import { memo, useMemo } from 'react';

import {
  getDeviceTileStateCapabilityId,
  getDeviceTileStateValue,
} from '../../lib/deviceQuickAction.ts';
import { noop } from '../../lib/noop';

import { useQuickAction } from '../../hooks/useQuickAction.js';
import { useCapabilityValue } from '../../store/devices/useCapabilityValue.ts';
import { useDevice } from '../../store/devices/useDevices.ts';
import { useApi } from '../../store/SelectedHomeyStore.hooks.ts';

import { DevicePremiumStatus } from './DevicePremiumStatus';
import {
  DeviceQuickAction,
  getDeviceQuickActionVariant,
  shouldRenderDeviceQuickAction,
} from './DeviceQuickAction';
import { DeviceTileStatic } from './DeviceTileStatic';
import { Indicator } from './Indicator';

export const MemoizedDeviceStatic = memo(function DeviceStatic(props) {
  const { api } = useApi();
  const { device: storedDevice } = useDevice({ deviceId: props.deviceId });
  const device = useMemo(() => {
    if (storedDevice == null) {
      return storedDevice;
    }

    const hasVirtualClassPreview = props.virtualClass !== undefined;
    const hasQuickActionPreview = props.quickActionOverride !== undefined;

    if (hasVirtualClassPreview !== true && hasQuickActionPreview !== true) {
      return storedDevice;
    }

    const previewDevice = { ...storedDevice };

    if (hasVirtualClassPreview === true) {
      previewDevice.virtualClass = props.virtualClass;
    }

    if (hasQuickActionPreview === true) {
      previewDevice.ui = {
        ...(storedDevice.ui ?? {}),
        quickActionOverride: props.quickActionOverride,
      };
    }

    return previewDevice;
  }, [props.quickActionOverride, props.virtualClass, storedDevice]);
  const {
    value,
    quickActionId: rawQuickActionId,
    quickActionTitle,
  } = useQuickAction({
    device,
  });

  const deviceName = device?.name.replace(/\s/g, ' ');
  const isLocked = device?.requiresPremium === true && api?.tier !== 'premium';
  const tileStateCapabilityId = getDeviceTileStateCapabilityId({
    device,
  });
  const [tileStateValue] = useCapabilityValue({
    deviceId: props.deviceId,
    capabilityId: tileStateCapabilityId,
  });
  const tileStateCapabilityObj =
    tileStateCapabilityId != null
      ? (device?.capabilitiesObj?.[tileStateCapabilityId] ?? null)
      : null;
  const isDeviceTileOn = getDeviceTileStateValue({
    capabilityObj: tileStateCapabilityObj,
    device,
    isLocked,
    value: tileStateValue,
  });
  const isQuickActionOverrideNone = device?.ui?.quickActionOverride === '.none';
  const quickActionId = isQuickActionOverrideNone === true ? null : rawQuickActionId;
  const quickActionCapabilityObj =
    quickActionId != null && device?.capabilitiesObj != null
      ? (device.capabilitiesObj[quickActionId] ?? null)
      : null;
  const quickActionVariant = getDeviceQuickActionVariant({
    capabilityObj: quickActionCapabilityObj,
    device,
  });
  const shouldRenderQuickAction = shouldRenderDeviceQuickAction({
    capabilityObj: quickActionCapabilityObj,
    device,
    isLocked,
    variant: quickActionVariant,
  });
  const quickActionBaseId = quickActionId?.split('.')[0] ?? null;
  const quickAction =
    shouldRenderQuickAction === true && quickActionBaseId != null && quickActionVariant != null ? (
      <DeviceQuickAction
        baseId={quickActionBaseId}
        capabilityObj={quickActionCapabilityObj}
        device={device}
        isDisabled={true}
        isActive={value === true}
        isTileOn={isDeviceTileOn}
        label={quickActionTitle}
        onPress={noop}
        variant={quickActionVariant}
      />
    ) : null;

  return (
    <DeviceTileStatic
      name={deviceName}
      deviceId={props.deviceId}
      device={device}
      iconObj={device?.iconObj}
      iconOverride={device?.iconOverride}
      indicator={
        isLocked === true ? (
          <DevicePremiumStatus />
        ) : (
          <Indicator device={device} tileSize={props.tileSize} />
        )
      }
      isLocked={isLocked}
      isOn={isDeviceTileOn}
      quickAction={quickAction}
      tileSize={props.tileSize}
    />
  );
});
